import {
  CRMConversationFilter,
  CRMConversationMode,
} from "@jugl-web/rest-api/integrations/types";
import { useTranslations } from "@jugl-web/utils";
import { FC, ReactNode } from "react";

const EMPTY_STATE_WRAPPER_CLASSNAME =
  "mx-auto mt-40 px-6 text-center text-[#828282] font-secondary text-sm leading-[160%]";

export type ConversationListEmptyStateProps = {
  hasConversations: boolean;
  searchQuery: string;
  selectedFilter?: CRMConversationFilter;
  selectedMode?: CRMConversationMode;
  emptyMessage?: ReactNode;
};

export const ConversationListEmptyState: FC<
  ConversationListEmptyStateProps
> = ({
  hasConversations,
  searchQuery,
  selectedFilter = CRMConversationFilter.active,
  selectedMode = CRMConversationMode.allChats,
  emptyMessage,
}) => {
  const { t } = useTranslations();

  const highlightedText = (text: ReactNode) => (
    <span className="text-primary-800">{text}</span>
  );

  if (hasConversations) {
    return null;
  }

  if (searchQuery) {
    return (
      <div className={EMPTY_STATE_WRAPPER_CLASSNAME}>
        {t(
          {
            id: "client-support-page.no-results-for-your-request",
            defaultMessage:
              "<highlightedText>No results</highlightedText> for your request",
          },
          { highlightedText }
        )}
      </div>
    );
  }

  if (emptyMessage) {
    return <div className={EMPTY_STATE_WRAPPER_CLASSNAME}>{emptyMessage}</div>;
  }

  if (selectedFilter === CRMConversationFilter.active) {
    let message: ReactNode = null;

    switch (selectedMode) {
      case CRMConversationMode.allChats:
        message = t(
          {
            id: "client-support-page.no-active-conversations-yet",
            defaultMessage:
              "No <highlightedText>active</highlightedText> conversations yet",
          },
          { highlightedText }
        );
        break;
      case CRMConversationMode.onAIAutoReply:
        message = t(
          {
            id: "client-support-page.no-chats-managed-by-ai-agent-yet",
            defaultMessage:
              "No chats <highlightedText>managed by AI Agent</highlightedText> yet",
          },
          { highlightedText }
        );
        break;
      case CRMConversationMode.waitingResponse:
        message = t(
          {
            id: "client-support-page.no-chats-await-manual-response-yet",
            defaultMessage:
              "No chats <highlightedText>awaits manual response</highlightedText> yet",
          },
          { highlightedText }
        );
        break;
      case CRMConversationMode.managedByMe:
        message = t(
          {
            id: "client-support-page.no-chats-managed-by-you-yet",
            defaultMessage:
              "No chats is <highlightedText>managed by You</highlightedText> yet",
          },
          { highlightedText }
        );
        break;
      default:
        break;
    }

    if (!message) {
      return null;
    }

    return <div className={EMPTY_STATE_WRAPPER_CLASSNAME}>{message}</div>;
  }

  return (
    <div className={EMPTY_STATE_WRAPPER_CLASSNAME}>
      {selectedFilter === CRMConversationFilter.expired
        ? t(
            {
              id: "client-support-page.no-expired-conversations-yet",
              defaultMessage:
                "No <highlightedText>expired</highlightedText> conversations yet",
            },
            { highlightedText }
          )
        : selectedFilter === CRMConversationFilter.closed
          ? t(
              {
                id: "client-support-page.no-closed-conversations-yet",
                defaultMessage:
                  "No <highlightedText>closed</highlightedText> conversations yet",
              },
              { highlightedText }
            )
          : t(
              {
                id: "client-support-page.no-blocked-conversations-yet",
                defaultMessage:
                  "No <highlightedText>blocked</highlightedText> conversations yet",
              },
              { highlightedText }
            )}
    </div>
  );
};
